import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchProgramList, fetchProcessorList } from "../../features/program/ProgramSlice";
import { fetchDistributors } from "../../features/distributor/DistributorSlice";
import { DateTypeDropdown } from "./filters/DateTypeDropdown";
import { TimezoneDropdown } from "./filters/TimezoneDropdown";
import { MultiSelectDropdown } from "./filters/MultiSelectDropdown";
import { channelOptions, reconOptions, voucherStates } from "./filters/options";
import type { DashboardFiltersState, VoucherStateFilter } from "./filters/types";

interface DashboardFiltersProps {
  filters: DashboardFiltersState;
  onFiltersChange: (filters: Partial<DashboardFiltersState>) => void;
  onFilterChange?: () => void;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  onFilterChange,
}) => {
  const dispatch = useAppDispatch();
  const { programList, processorList } = useAppSelector((state) => state.program);
  const { distributorList } = useAppSelector((state) => state.distributor);

  const [dateTypeDropdownOpen, setDateTypeDropdownOpen] = useState(false);
  const [timezoneDropdownOpen, setTimezoneDropdownOpen] = useState(false);
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const [distributorDropdownOpen, setDistributorDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const [processorDropdownOpen, setProcessorDropdownOpen] = useState(false);
  const [reconDropdownOpen, setReconDropdownOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProgramList({}));
    dispatch(fetchDistributors(null));
    dispatch(fetchProcessorList());
  }, [dispatch]);

  const processorFilterOptions = useMemo(
    () => (processorList || []).map((p) => ({ value: p.processorId, label: p.name || p.processorType })),
    [processorList],
  );

  const {
    dateFilterType,
    timezone,
    selectedProgram,
    selectedDistributor,
    selectedChannel,
    selectedProcessor,
    selectedState,
    selectedRecon,
  } = filters;

  const programOptions = useMemo(
    () =>
      (programList || []).map((program: any) => ({
        value: program.programId as number,
        label: program.programName as string,
      })),
    [programList]
  );

  const distributorOptions = useMemo(
    () =>
      (distributorList || []).map((distributor: any) => ({
        value: distributor.distributorId as number,
        label: distributor.name as string,
      })),
    [distributorList]
  );

  const apply = (updates: Partial<DashboardFiltersState>) => {
    onFiltersChange(updates);
    onFilterChange?.();
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <DateTypeDropdown
          value={dateFilterType}
          open={dateTypeDropdownOpen}
          setOpen={setDateTypeDropdownOpen}
          onChange={(value) => apply({ dateFilterType: value })}
        />

        <TimezoneDropdown
          value={timezone}
          open={timezoneDropdownOpen}
          setOpen={setTimezoneDropdownOpen}
          onChange={(value) => apply({ timezone: value })}
        />

        <MultiSelectDropdown<number>
          label="Program"
          allLabel="All Programs"
          selected={selectedProgram}
          options={programOptions}
          open={programDropdownOpen}
          setOpen={setProgramDropdownOpen}
          onChange={(value) => apply({ selectedProgram: value })}
        />

        <MultiSelectDropdown<number>
          label="Distributor"
          allLabel="All Distributors"
          selected={selectedDistributor}
          options={distributorOptions}
          open={distributorDropdownOpen}
          setOpen={setDistributorDropdownOpen}
          onChange={(value) => apply({ selectedDistributor: value })}
        />

        <MultiSelectDropdown<VoucherStateFilter>
          label="Status"
          allLabel="All States"
          selected={selectedState}
          options={voucherStates}
          open={stateDropdownOpen}
          setOpen={setStateDropdownOpen}
          onChange={(value) => apply({ selectedState: value })}
        />

        <MultiSelectDropdown<number>
          label="Channel"
          allLabel="All Channels"
          selected={selectedChannel}
          options={channelOptions}
          open={channelDropdownOpen}
          setOpen={setChannelDropdownOpen}
          onChange={(value) => apply({ selectedChannel: value })}
        />

        <MultiSelectDropdown<number>
          label="Processor"
          allLabel="All Processors"
          selected={selectedProcessor}
          options={processorFilterOptions}
          open={processorDropdownOpen}
          setOpen={setProcessorDropdownOpen}
          onChange={(value) => apply({ selectedProcessor: value })}
        />

        <MultiSelectDropdown<number>
          label="Recon"
          allLabel="All Recon Status"
          selected={selectedRecon}
          options={reconOptions}
          open={reconDropdownOpen}
          setOpen={setReconDropdownOpen}
          onChange={(value) => apply({ selectedRecon: value })}
        />
      </div>
    </div>
  );
};

export type { DashboardFiltersState } from "./filters/types";
export default DashboardFilters;
